import range from 'lodash/range'

export const pageSize = 10

export const pageSlugRegex = /^page-(\d+)$/

export function isSlugAPageNumber(slug?: string | string[]) {
  if (typeof slug === 'string') {
    return pageSlugRegex.test(slug)
  }
  return false
}

export function extractPageNumberFromParam(slug: string): number | undefined {
  if (isSlugAPageNumber(slug)) {
    const result = slug.match(pageSlugRegex)
    if (Array.isArray(result) && result[1]) {
      const pageNumberAsString = result[1]
      const pageNumber = parseInt(pageNumberAsString, 10)
      if (Number.isNaN(pageNumber)) return undefined
      return pageNumber
    }
  }
  return undefined
}

export function calculatePageCount(itemCount: number, size: number) {
  return Math.ceil(itemCount / size)
}

export interface FetchPageOptions {
  offset: number
  limit: number
}

/**
 * Generic paginated data fetcher.
 * The results from the first page fetch will have an indication of the total in it
 * that can be extracted with a supplied function.
 * We calculate how many further fetches are required using this total, along with the required offsets.
 * We then make all those fetches, finally extracting the results into a single array of the
 * required item type using the supplied item extraction function.
 */
export async function paginatedFetcher<FetchResults, Item>(
  fetchPage: (options: FetchPageOptions) => Promise<FetchResults>,
  itemCountExtractor: (fetchResults: FetchResults) => number,
  itemExtractor: (fetchResults: FetchResults) => Item[],
  itemsPerPage: number,
): Promise<Item[]> {
  if (itemsPerPage < 1) return [] // guard against inability to express the Naturals in the type system
  const firstPage = await fetchPage({ offset: 0, limit: itemsPerPage })
  const itemCount = itemCountExtractor(firstPage)
  const pageCount = Math.ceil(itemCount / itemsPerPage)
  const remainingRangeOfPageNumbersToFetch =
    pageCount > 1 ? range(1, pageCount) : []
  const subsequentPagesPromises = remainingRangeOfPageNumbersToFetch.map(
    (pageNumber) =>
      fetchPage({ offset: pageNumber * itemsPerPage, limit: itemsPerPage }),
  )
  const subsequentPages = await Promise.all(subsequentPagesPromises)
  const pages = [firstPage, ...subsequentPages]
  return pages.flatMap(itemExtractor)
}
